import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { TenantLogo } from "@/components/TenantLogo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDeShort } from "@/lib/date";
import { toast } from "sonner";
import { CheckCircle2, Clock, Video, MapPin } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";

export default function CoachSignPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [signature, setSignature] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  const padRef = useRef(null);

  useEffect(() => {
    api.get(`/appointments/coach-sign/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [token]);

  const submit = async () => {
    if (!signature) { toast.error("Bitte im Feld unterschreiben"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/appointments/coach-sign/${token}`, { signature });
      setDone(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      padRef.current?.clear();
      setSignature(null);
    } finally { setSaving(false); }
  };

  if (error) return (
    <Shell>
      <div className="text-xs font-bold uppercase tracking-overline text-red-600 mb-2">Fehler</div>
      <div className="text-sm text-slate-700" data-testid="coach-sign-error">{error}</div>
    </Shell>
  );
  if (!info) return <Shell><div className="text-sm text-slate-500">Lädt…</div></Shell>;

  if (done || info.already_signed) {
    return (
      <Shell>
        <div className="text-center py-4" data-testid="coach-sign-done">
          <CheckCircle2 size={38} strokeWidth={1} className="mx-auto text-emerald-600 mb-3" />
          <h2 className="font-heading text-2xl font-light tracking-tight text-slate-900">Danke, {info.coach_name}!</h2>
          <p className="text-sm text-slate-600 mt-2">
            {done?.signed_count > 1
              ? `${done.signed_count} Termine sind jetzt mit deiner Unterschrift versehen.`
              : "Deine Unterschrift ist gespeichert und erscheint ab sofort auf der Anwesenheitsliste."}
          </p>
        </div>
      </Shell>
    );
  }

  const rows = info.appointments || [];
  return (
    <Shell>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Unterschrift Coach</div>
      <h1 className="font-heading text-3xl font-light tracking-tighter text-slate-900 mt-2">Hallo {info.coach_name}</h1>
      <p className="text-sm text-slate-600 mt-2" data-testid="coach-sign-reason">
        {info.requested_by || "Die Verwaltung"} bittet dich, {rows.length === 1 ? "diesen Termin" : "diese Termine"} selbst zu
        unterschreiben – {info.replaced ? "bisher war dort eine falsche Unterschrift hinterlegt" : "deine Unterschrift fehlt dort noch"}.
        Bereits vorhandene Unterschriften bleiben unverändert.
      </p>

      <div className="border border-slate-200 bg-white divide-y divide-slate-100 mt-5" data-testid="coach-sign-details">
        {rows.map((a) => {
          const ModeIcon = a.mode === "online" ? Video : MapPin;
          return (
            <div key={a.appointment_id} className="p-4 flex items-center gap-3" data-testid={`coach-sign-row-${a.appointment_id}`}>
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-900">{a.client_name}</div>
                <div className="text-xs text-slate-600 font-mono mt-1 flex items-center gap-3 flex-wrap">
                  <span>{formatDeShort(a.date)}</span>
                  {(a.time_from || a.time_to) && <span className="flex items-center gap-1"><Clock size={11} strokeWidth={1.5} />{a.time_from}–{a.time_to}</span>}
                  <span>{a.planned_ue} UE</span>
                  <span className="flex items-center gap-1"><ModeIcon size={11} strokeWidth={1.5} />{a.mode === "online" ? "Remote" : "Präsenz"}</span>
                </div>
                {a.topic && <div className="text-xs text-slate-500 italic mt-1">{a.topic}</div>}
              </div>
              {a.already_signed && (
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200">
                  bereits unterschrieben
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-5">
        <SignaturePad ref={padRef} onChange={setSignature} />
      </div>
      <button onClick={submit} disabled={saving || !signature} data-testid="coach-sign-submit"
        className="mt-5 w-full px-4 py-3.5 bg-slate-900 text-white text-sm font-medium hover:bg-black disabled:opacity-40">
        {saving ? "Wird gespeichert…" : info.open_count > 1 ? `${info.open_count} Termine unterschreiben` : "Unterschrift speichern"}
      </button>
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 px-4 py-10">
    <div className="max-w-[560px] mx-auto">
      <div className="mb-6"><TenantLogo /></div>
      {children}
    </div>
  </div>
);
